import { useState, useEffect } from 'react'
import { getDungeonSaves, createDungeonSave, deleteDungeonSave } from 'actions/api'
import { spriteImagePath } from '../../config/constants'
import {
  LockScroll,
  Overlay,
  Panel,
  PanelTitle,
  Divider,
  SectionLabel,
  SaveCard,
  SaveInfo,
  SaveName,
  SaveMeta,
  DeleteBtn,
  NewGameBtn,
  Label,
  Input,
  ClassGrid,
  ClassCard,
  ClassName,
  ClassDesc,
  SpritePreview,
  Actions,
  Btn,
  ErrorMsg,
} from './styles'

// ── Starting stats by class ──────────────────────────────────────────────────
const CLASSES = [
  {
    id:          'cowboy',
    label:       'Cowboy',
    sprite:      'Cowboy',
    description: 'Tough frontliner, high HP',
    stats:       { health: 120, max_health: 120, level: 1, xp: 0, score: 0, damage: 14, speed: 2 },
  },
  {
    id:          'cowgirl',
    label:       'Cowgirl',
    sprite:      'Cowgirl',
    description: 'Fast and precise',
    stats:       { health: 95,  max_health: 95,  level: 1, xp: 0, score: 0, damage: 13, speed: 4 },
  },
  {
    id:          'sailor',
    label:       'Sailor',
    sprite:      'Sailor',
    description: 'Tough brawler, heavy hits',
    stats:       { health: 110, max_health: 110, level: 1, xp: 0, score: 0, damage: 16, speed: 3 },
  },
  {
    id:          'mermaid',
    label:       'Mermaid',
    sprite:      'Mermaid',
    description: 'Glass cannon, high damage',
    stats:       { health: 80,  max_health: 80,  level: 1, xp: 0, score: 0, damage: 22, speed: 3 },
  },
  {
    id:          'hula',
    label:       'Hula Girl',
    sprite:      'Hula',
    description: 'Nimble explorer, very fast',
    stats:       { health: 90,  max_health: 90,  level: 1, xp: 0, score: 0, damage: 11, speed: 5 },
  },
]

// Global localStorage key — active save applies across all dungeons
const ACTIVE_SAVE_KEY = 'dungeon_active_save'

export const getActiveSave = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_SAVE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

export const setActiveSave = (save) => {
  if (!save) return
  localStorage.setItem(ACTIVE_SAVE_KEY, JSON.stringify(save))
}

export const clearActiveSave = () => {
  localStorage.removeItem(ACTIVE_SAVE_KEY)
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const classMeta = (save) => {
  const cls = CLASSES.find((c) => c.id === save.character?.class)
  return cls ? cls.label : save.character?.class || 'Unknown'
}

// ── Component ─────────────────────────────────────────────────────────────────
export const CharacterSelect = ({ onEnter, onCancel }) => {
  const [saves,     setSaves]     = useState(null)
  const [error,     setError]     = useState(null)
  const [creating,  setCreating]  = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [charName,   setCharName]   = useState('')
  const [charClass,  setCharClass]  = useState(CLASSES[0].id)
  const selectedClass = CLASSES.find((c) => c.id === charClass)

  useEffect(() => {
    getDungeonSaves()
      .then((data) => setSaves(data.saves ?? []))
      .catch(() => { setSaves([]); setError('Could not load saves.') })
  }, [])

  const handleContinue = (save) => {
    // Prefer localStorage save when IDs match — it's always more current than the
    // API response (exit writes to localStorage synchronously; API is async best-effort).
    const local = getActiveSave()
    const merged = (local?.id === save.id)
      ? { ...save, stats: local.stats, inventory: local.inventory }
      : save
    setActiveSave(merged)
    onEnter(merged)
  }

  const handleDelete = (e, save) => {
    e.stopPropagation()
    if (!window.confirm(`Delete "${save.name || save.character?.name || 'this save'}"?`)) return
    deleteDungeonSave(save.id)
      .then(() => setSaves((prev) => prev.filter((s) => s.id !== save.id)))
      .catch((err) => setError(err?.message || 'Could not delete save.'))
  }

  const handleCreate = () => {
    const name = charName.trim()
    if (!name) return

    setSubmitting(true)
    setError(null)
    createDungeonSave({
      name:      `${name} — ${selectedClass.label}`,
      character: { name, class: charClass, sprite: selectedClass.sprite },
      stats:     selectedClass.stats,
      inventory: [],
      position:  null,
    })
      .then((data) => {
        if (!data?.save) {
          const msg = data?.error || 'Could not create save.'
          throw new Error(msg)
        }
        setActiveSave(data.save)
        onEnter(data.save)
      })
      .catch((err) => {
        setError(err?.message || 'Could not create save.')
        setSubmitting(false)
      })
  }

  const loading = saves === null

  return (
    <Overlay onClick={onCancel}>
      <LockScroll />
      <Panel onClick={(e) => e.stopPropagation()}>
        <PanelTitle>Select Character</PanelTitle>

        {/* ── Existing saves ── */}
        {!creating && (
          <>
            {loading && <SectionLabel>Loading saves…</SectionLabel>}

            {!loading && saves.length > 0 && (
              <>
                <SectionLabel>Continue</SectionLabel>
                {saves.map((save) => (
                  <SaveCard key={save.id} onClick={() => handleContinue(save)}>
                    {save.character?.sprite && (
                      <img
                        src={spriteImagePath(save.character.sprite)}
                        alt={save.character.sprite}
                        style={{ width: 32, height: 32, imageRendering: 'pixelated', objectFit: 'contain', flexShrink: 0 }}
                      />
                    )}
                    <SaveInfo>
                      <SaveName>{save.character?.name || save.name || 'Unnamed'}</SaveName>
                      <SaveMeta>
                        {classMeta(save)}
                        {save.stats?.level ? ` · Lv ${save.stats.level}` : ''}
                        {save.stats?.health != null ? ` · ${save.stats.health} HP` : ''}
                        {save.updated_at ? ` · ${formatDate(save.updated_at)}` : ''}
                      </SaveMeta>
                    </SaveInfo>
                    <DeleteBtn onClick={(e) => handleDelete(e, save)} title="Delete save">✕</DeleteBtn>
                  </SaveCard>
                ))}
                <Divider />
              </>
            )}

            {!loading && (
              <NewGameBtn onClick={() => setCreating(true)}>
                + New Character
              </NewGameBtn>
            )}

            {error && <ErrorMsg>{error}</ErrorMsg>}

            <Actions>
              <Btn onClick={onCancel}>Cancel</Btn>
            </Actions>
          </>
        )}

        {/* ── New character form ── */}
        {creating && (
          <>
            <SectionLabel>New Character</SectionLabel>

            <Label>Character Name</Label>
            <Input
              autoFocus
              value={charName}
              onChange={(e) => setCharName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="Enter a name…"
              maxLength={48}
            />

            <Label>Class</Label>
            <SpritePreview>
              <img
                src={spriteImagePath(selectedClass?.sprite)}
                alt={charClass}
                style={{ imageRendering: 'pixelated', objectFit: 'contain' }}
              />
            </SpritePreview>
            <ClassGrid>
              {CLASSES.map((cls) => (
                <ClassCard
                  key={cls.id}
                  selected={charClass === cls.id}
                  onClick={() => setCharClass(cls.id)}
                  type="button"
                >
                  <ClassName>{cls.label}</ClassName>
                  <ClassDesc>{cls.description}</ClassDesc>
                </ClassCard>
              ))}
            </ClassGrid>

            {error && <ErrorMsg>{error}</ErrorMsg>}

            <Actions>
              <Btn onClick={() => { setCreating(false); setError(null) }}>
                Back
              </Btn>
              <Btn
                primary
                disabled={!charName.trim() || submitting}
                onClick={handleCreate}
              >
                {submitting ? 'Creating…' : 'Begin'}
              </Btn>
            </Actions>
          </>
        )}
      </Panel>
    </Overlay>
  )
}
